// A4 period report document for the monitoring section (day / month / year),
// rendered with @react-pdf/renderer. Replaces the old hand-positioned jsPDF
// output: branded header, 4-up stat grid, vector chart (./charts), breakdown
// table, and a fixed footer with page numbers on every page.
//
// Payload contract (built by MonitoringSection.handleDownload):
//   report: { kind: "day",   label, day: DayData }
//           { kind: "month", label, year, month, data: MonthData }
//           { kind: "year",  label, year, data: YearData }
//   meta:   { systemKwp, city, installDate, lifetimeKwh, lifetimeCo2Kg } -
//           live values in live mode; mock constants fill in standalone.

import { Document, Image, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import {
  SYSTEM_KWP,
  LIFETIME,
  MONTH_LONG,
  MONTH_SHORT,
  fmtRs,
  fmtDate,
  INSTALL_DATE,
} from "../mockData";
import { COLORS, BODY_FONT, DISPLAY_FONT } from "./theme";
import { PowerCurve, BarsChart } from "./charts";

const round1 = (n) => Math.round(n * 10) / 10;
const fmtKwh = (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 1 });
const logoSrc = () => `${process.env.PUBLIC_URL || ""}/greentek-logo.png`;

const s = StyleSheet.create({
  page: {
    paddingTop: 44,
    paddingHorizontal: 48,
    paddingBottom: 78,
    fontFamily: BODY_FONT,
    fontSize: 9.5,
    color: COLORS.ink,
  },

  // Header
  brandRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gold,
    paddingBottom: 8,
  },
  brand: {
    fontSize: 8.5,
    fontWeight: 700,
    letterSpacing: 2.6,
    color: COLORS.deepGold,
    textTransform: "uppercase",
  },
  logo: { width: 72, height: 38.25, objectFit: "contain" },
  brandRight: { fontSize: 7, letterSpacing: 2, color: COLORS.muted, textTransform: "uppercase" },
  titleRow: { flexDirection: "row", justifyContent: "space-between", marginTop: 18 },
  title: { fontFamily: DISPLAY_FONT, fontWeight: 700, fontSize: 24, color: COLORS.ink },
  plant: { marginTop: 7, fontSize: 10, fontWeight: 600, color: COLORS.ink },
  periodBlock: { alignItems: "flex-end" },
  period: { fontFamily: DISPLAY_FONT, fontWeight: 600, fontSize: 14, color: COLORS.deepGold },
  metaLine: { marginTop: 4, fontSize: 7.5, color: COLORS.muted },

  // Stat grid
  statGrid: { flexDirection: "row", marginTop: 22 },
  statCell: {
    flex: 1,
    backgroundColor: COLORS.paleGold,
    borderRadius: 5,
    paddingVertical: 11,
    paddingHorizontal: 12,
    marginRight: 10,
  },
  statCellLast: { marginRight: 0 },
  statLabel: {
    fontSize: 6.8,
    fontWeight: 600,
    letterSpacing: 1.1,
    textTransform: "uppercase",
    color: COLORS.muted,
  },
  statValueRow: { flexDirection: "row", alignItems: "flex-end", marginTop: 6 },
  statValue: { fontSize: 16, fontWeight: 700, color: COLORS.ink },
  statUnit: { fontSize: 8.5, color: COLORS.muted, marginLeft: 3, paddingBottom: 1.5 },

  highlights: { marginTop: 12, fontSize: 8.5, color: COLORS.muted, lineHeight: 1.5 },

  // Sections
  section: { marginTop: 22 },
  sectionTitle: {
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: 1.6,
    textTransform: "uppercase",
    color: COLORS.deepGold,
    marginBottom: 8,
  },

  // Table
  tableHead: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gold,
    paddingBottom: 5,
  },
  tableHeadText: {
    fontSize: 7,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: COLORS.muted,
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4.5,
    paddingHorizontal: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: COLORS.hairline,
  },
  tableRowAlt: { backgroundColor: COLORS.parchment },
  colLabel: { flex: 1.6 },
  colNum: { flex: 1, textAlign: "right" },
  cellLabel: { fontSize: 9, color: COLORS.ink },
  cellValue: { fontSize: 9, fontWeight: 600, color: COLORS.ink },
  cellMuted: { fontSize: 9, color: COLORS.muted },

  // Fixed footer
  footer: { position: "absolute", left: 48, right: 48, bottom: 30 },
  footerLifetime: { fontSize: 7.5, color: COLORS.muted, marginBottom: 7 },
  footerInner: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTopWidth: 0.8,
    borderTopColor: COLORS.gold,
    paddingTop: 7,
  },
  footerText: { fontSize: 7.5, color: COLORS.muted },
});

function StatCell({ label, value, unit, last }) {
  return (
    <View style={[s.statCell, last && s.statCellLast]}>
      <Text style={s.statLabel}>{label}</Text>
      <View style={s.statValueRow}>
        <Text style={s.statValue}>{value}</Text>
        {unit ? <Text style={s.statUnit}>{unit}</Text> : null}
      </View>
    </View>
  );
}

function DataTable({ headers, rows }) {
  return (
    <View>
      <View style={s.tableHead}>
        <Text style={[s.tableHeadText, s.colLabel]}>{headers[0]}</Text>
        <Text style={[s.tableHeadText, s.colNum]}>{headers[1]}</Text>
        <Text style={[s.tableHeadText, s.colNum]}>{headers[2]}</Text>
      </View>
      {rows.map(([label, value, saved], i) => (
        <View key={i} style={[s.tableRow, i % 2 === 1 && s.tableRowAlt]} wrap={false}>
          <Text style={[s.cellLabel, s.colLabel]}>{label}</Text>
          <Text style={[s.cellValue, s.colNum]}>{value}</Text>
          <Text style={[s.cellMuted, s.colNum]}>{saved}</Text>
        </View>
      ))}
    </View>
  );
}

export function ReportDocument({ report, tariff, meta = {} }) {
  const systemKwp = meta.systemKwp ?? SYSTEM_KWP;
  const city = meta.city ?? "Rajkot";
  const installDate = meta.installDate
    ? new Date(meta.installDate)
    : Object.prototype.hasOwnProperty.call(meta, "installDate")
      ? null
      : INSTALL_DATE;
  const lifetimeKwh = meta.lifetimeKwh ?? LIFETIME.kwh;
  const lifetimeCo2Kg = meta.lifetimeCo2Kg ?? LIFETIME.co2Kg;

  const isDay = report.kind === "day";
  const d = isDay ? report.day : report.data;

  const stats = [
    { label: "Energy generated", value: fmtKwh(d.kwh), unit: "kWh" },
    { label: "Money saved", value: fmtRs(d.savedRs), unit: "" },
    { label: "CO₂ avoided", value: fmtKwh(d.co2Kg), unit: "kg" },
    {
      label: "Peak sun hours",
      value: isDay
        ? fmtKwh(d.sunHours)
        : systemKwp > 0
          ? fmtKwh(round1(d.kwh / systemKwp))
          : "Not reported",
      unit: isDay || systemKwp > 0 ? "h" : "",
    },
  ];

  let highlights;
  let chartTitle;
  let chart = null;
  let tableHeaders;
  let tableRows = [];

  if (isDay) {
    highlights = `Peak ${d.peakKw} kW at ${d.peakTime}`;
    chartTitle = "Hourly power curve (kW)";
    chart = <PowerCurve curve={d.curve} />;
    tableHeaders = ["Hour", "Energy (kWh)", "Saved"];
    tableRows = d.curve
      .map((kw, h) => [`${String(h).padStart(2, "0")}:00`, kw])
      .filter(([, kw]) => kw > 0)
      .map(([label, kw]) => [label, fmtKwh(kw), fmtRs(Math.round(kw * tariff))]);
  } else if (report.kind === "month") {
    const { days, bestDay } = d;
    const avg = round1(d.kwh / Math.max(days.length, 1));
    highlights = `Best day ${bestDay.day} ${MONTH_SHORT[report.month]} · ${fmtKwh(bestDay.kwh)} kWh · Daily average ${fmtKwh(avg)} kWh`;
    chartTitle = "Daily generation (kWh)";
    const bars = days.map((day) => ({
      label: day.day === 1 || day.day % 5 === 0 ? String(day.day) : "",
      value: day.kwh,
      highlight: day.day === bestDay.day,
    }));
    chart = <BarsChart bars={bars} />;
    tableHeaders = ["Date", "Energy (kWh)", "Saved"];
    tableRows = days.map((day) => [
      `${day.day} ${MONTH_SHORT[report.month]}`,
      fmtKwh(day.kwh),
      fmtRs(Math.round(day.kwh * tariff)),
    ]);
  } else {
    const { months, bestMonth } = d;
    const avg = round1(d.kwh / Math.max(months.length, 1));
    highlights = `Best month ${MONTH_LONG[bestMonth.month]} · ${fmtKwh(bestMonth.kwh)} kWh · Monthly average ${fmtKwh(avg)} kWh`;
    chartTitle = "Monthly generation (kWh)";
    const bars = months.map((m) => ({
      label: MONTH_SHORT[m.month],
      value: m.kwh,
      highlight: m.month === bestMonth.month,
    }));
    chart = <BarsChart bars={bars} height={130} />;
    tableHeaders = ["Month", "Energy (kWh)", "Saved"];
    tableRows = months.map((m) => [
      MONTH_LONG[m.month],
      fmtKwh(m.kwh),
      fmtRs(Math.round(m.kwh * tariff)),
    ]);
  }

  return (
    <Document
      title={`Solar Generation Report · ${report.label}`}
      author="Greentek India Limited"
      subject="Solar plant generation report"
    >
      <Page size="A4" style={s.page}>
        <View style={s.brandRow}>
          <Image src={logoSrc()} style={s.logo} />
          <Text style={s.brandRight}>Solar Monitoring</Text>
        </View>

        <View style={s.titleRow}>
          <View>
            <Text style={s.title}>Solar Generation Report</Text>
            <Text style={s.plant}>{`Residential rooftop · ${
              systemKwp > 0 ? `${systemKwp} kWp` : "Capacity not reported"
            } · ${city || "Location not reported"}`}</Text>
          </View>
          <View style={s.periodBlock}>
            <Text style={s.period}>{report.label}</Text>
            <Text style={s.metaLine}>{`Generated ${fmtDate(new Date())}`}</Text>
            <Text style={s.metaLine}>{`${
              installDate ? `Installed ${fmtDate(installDate)}` : "Installation date not reported"
            } · Tariff ${fmtRs(tariff)}/kWh`}</Text>
          </View>
        </View>

        <View style={s.statGrid}>
          {stats.map((stat, i) => (
            <StatCell key={stat.label} {...stat} last={i === stats.length - 1} />
          ))}
        </View>

        <Text style={s.highlights}>{highlights}</Text>

        {chart && (
          <View style={s.section} wrap={false}>
            <Text style={s.sectionTitle}>{chartTitle}</Text>
            {chart}
          </View>
        )}

        {tableRows.length > 0 && (
          <View style={s.section}>
            <Text style={s.sectionTitle}>Breakdown</Text>
            <DataTable headers={tableHeaders} rows={tableRows} />
          </View>
        )}

        <View style={s.footer} fixed>
          <Text style={s.footerLifetime}>
            {`Lifetime since installation: ${(lifetimeKwh / 1000).toFixed(1)} MWh · ${fmtRs(Math.round(lifetimeKwh * tariff))} saved · ${(lifetimeCo2Kg / 1000).toFixed(1)} t CO₂ avoided`}
          </Text>
          <View style={s.footerInner}>
            <Text style={s.footerText}>Greentek India Limited · alliance.greentekindia.co.in</Text>
            <Text
              style={s.footerText}
              render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
}
